import React, { useEffect, useMemo, useState } from "react"
import { Form, Formik } from "formik"
import { usePermissions } from "hooks"
import { Filter, FilterX, Trash, ListCheck } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useLocation } from "react-router-dom"
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Modal,
  ModalBody,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Table,
} from "reactstrap"

import CustomField from "components/Common/CustomField"
import CustomLabel from "components/Common/CustomLabel"
import DeleteModal from "components/Common/DeleteModal"
import Paginations from "components/Common/Pagination"
import BackButton from "components/Shared/BackButton"
import MarkAttendance from "./MarkAttendance"
import Spinners from "components/Shared/Spinners"
import {
  addPracticalTrainingParticipant,
  deletePracticalTrainingParticipant,
  getPracticalTrainingParticipants,
} from "store/actions/practicalTrainingAction"
import {
  setParticipantsPayload as setPayload,
  resetParticipantsPayload as resetPayload,
} from "store/reducers/practicalTrainingReducer"

import { showAlert } from "store/reducers/alertReducer"
import * as Yup from "yup"
import axios from "axios"
import { API_URL } from "components/Common/Utils"
import { getJobTitlesList } from "store/actions/commonAction"
import Filters from "components/Shared/Filters"
import { hideAttendance } from "pages/TheoreticalTraining/helpers"
import MarkAssessmentModal from "./components/MarkAssessmentModal"

const navItems = [
  {
    id: 0,
    name: "All",
  },
  {
    id: 1,
    name: "Enrolled",
  },
  {
    id: 2,
    name: "Attendance",
  },
  {
    id: 3,
    name: "Passed",
  },
  {
    id: 4,
    name: "Failed",
  },
  {
    id: 5,
    name: "Absent",
  },
]

const PracticalTrainingParticipants = () => {
  const {
    loading,
    isLoading,
    practicalParticipants,
    totalPages,
    totalCount,
    currentPage,
    participantsListPayload: payload,
    attendance_count,
    participants_count,
  } = useSelector(state => state.PracticalTraining)

  const {
    t,
    i18n: { language },
  } = useTranslation()
  const dispatch = useDispatch()
  const { permissions, showAll } = usePermissions("practical_training")
  const { search, state } = useLocation()
  const { jobTitlesList } = useSelector(state => state.Common)

  const uuid = new URLSearchParams(search).get("uuid")
  const practicalTrainingName = new URLSearchParams(search).get("name")

  const [modal, setModal] = useState(false)
  const [showAssessmentModal, setShowAssmentModal] = useState(false)
  const toggle = () => setModal(!modal)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const [trigger, setTrigger] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [isShowFilter, setIsShowFilter] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [attendanceModal, setAttendanceModal] = useState(false)
  const [selectedUuid, setSelectedUuid] = useState(null)

  useEffect(() => {
    dispatch(getJobTitlesList())
  }, [])

  const toggleFilterVisibility = () => {
    setIsShowFilter(!isShowFilter)
  }

  useEffect(() => {
    getAllData()
  }, [trigger, activeTab])

  useEffect(() => {
    if (uuid) {
      dispatch(setPayload({ training_uuid: uuid }))
    }
  }, [uuid])

  const getAllData = () => {
    let data = {
      ...payload,
      training_uuid: uuid,
      filters: {
        ...payload.filters,
        type: activeTab,
      },
    }
    dispatch(getPracticalTrainingParticipants(data))
  }

  const handleAddParticipant = values => {
    const { national_ids } = values
    const data = {
      training_uuid: uuid, // training uuid
      national_ids: [national_ids],
    }
    dispatch(addPracticalTrainingParticipant(data))
      .then(action => {
        const { statusCode, success, message, data } = action?.payload
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }))
          setTrigger(!trigger)
          toggle()
        } else {
          dispatch(showAlert({ text: message, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const validationSchema = Yup.object().shape({
    national_ids: Yup.string()
      .matches(/^\d{10}$/, t("National ID must be exactly 10 digits"))
      .required("Field is required"),
  })

  const handleDelete = () => {
    dispatch(deletePracticalTrainingParticipant(itemIdToDelete))
      .then(action => {
        const { statusCode, success, message, data } = action?.payload
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }))
          setTrigger(!trigger)
          setShowDeleteModal()
        } else {
          dispatch(showAlert({ text: message, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const onPageChange = page => {
    dispatch(setPayload({ page_number: page }))
  }

  const toggleTab = tab => {
    if (activeTab !== tab) {
      setActiveTab(tab)
      dispatch(resetPayload())
    }
  }

  const handleShowMarkAttendanceButton = () => {
    if (!state) return true
    return !hideAttendance(state.end_date, state.end_time)
  }

  const handleExportXLSX = async () => {
    try {
      setIsExporting(true)
      const authUserString = localStorage.getItem("authUser")
      const authUser = authUserString ? JSON.parse(authUserString) : null
      const token = authUser?.token

      let data = {
        export_type: "practical_participants_list",
        filters: {
          training_uuid: uuid,
          keyword: payload.filters.keyword || "",
          type: activeTab == 0 ? null : activeTab,
          job_title_ids: payload.filters.job_title_ids,
        },
      }

      const request = "api/v1/admin/export/by-type"

      const response = await axios.post(`${API_URL}/${request}`, data, {
        headers: {
          Authorization: token, // Attach token in headers
        },
        responseType: "blob", // Important for file downloads
      })

      const contentType = response.headers["content-type"]

      if (contentType === "application/json") {
        return dispatch(
          showAlert({
            text: t("Export initiated, you will be notified once done."),
            type: "success",
          })
        )
      }

      // Create a URL for the downloaded file
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute(
        "download",
        activeTab === 0
          ? "All Participants.xlsx"
          : activeTab === 1
          ? "Enrolled Participants.xlsx"
          : activeTab === 2
          ? "Attended Participants.xlsx"
          : activeTab === 3
          ? "Passed Participants.xlsx"
          : activeTab === 4
          ? "Failed Participants.xlsx"
          : activeTab === 5
          ? "Absent Participants.xlsx"
          : "Not Invited Participants.xlsx"
      ) // Set filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url) // Clean up memory
    } catch (error) {
      console.error("Export failed:", error.response?.data || error.message)
    } finally {
      setIsExporting(false)
    }
  }

  const formattedJobTitles = useMemo(() => {
    if (!jobTitlesList) return []

    return jobTitlesList.map(jobTitle => ({
      value: jobTitle.id,
      label: jobTitle[language === "en" ? "name" : "name_ar"],
    }))
  }, [jobTitlesList])

  const filters = useMemo(() => {
    return {
      keyword: {
        type: "text",
        placeholder: t("Search by name,email,national id or phone number"),
        label: t("Search By"),
        colSize: 4,
      },
      job_title_ids: {
        type: "select",
        placeholder: t("Select Job Title"),
        label: t("Job Title"),
        colSize: 4,
        options: formattedJobTitles,
        multiple: true,
      },
    }
  }, [t])

  const handleReset = () => {
    dispatch(resetPayload())
    setTrigger(!trigger)
  }

  return (
    <React.Fragment>
      <div className="page-content">
        {loading ? (
          <Spinners />
        ) : (
          <Container fluid>
            <Row>
              <Col xl="12">
                <div className="d-flex justify-content-between mb-4 align-items-center">
                  <h4 className="card-title ">
                    <BackButton />
                    {t("Participants")}
                  </h4>
                  <div className="d-flex align-items-center gap-2">
                    {(showAll || permissions.includes("can_mark_attendance")) &&
                      handleShowMarkAttendanceButton() && (
                        <button
                          className="main_btn fit_content"
                          onClick={() => {
                            setAttendanceModal(true)
                          }}
                        >
                          {t("Mark Attendance")}
                        </button>
                      )}
                    {(showAll ||
                      permissions.includes("can_add_participants")) && (
                      <div>
                        <button
                          className="main_btn"
                          onClick={() => {
                            toggle()
                          }}
                        >
                          {t("Add Participant")}
                        </button>
                      </div>
                    )}
                    <button
                      className="main_btn fit_content"
                      onClick={handleExportXLSX}
                      disabled={isExporting}
                    >
                      {t("Download XLSX")}
                    </button>
                    <Button
                      color="primary"
                      onClick={() => toggleFilterVisibility()}
                      title={t("Show/Hide Filter")}
                    >
                      {isShowFilter ? <FilterX /> : <Filter />}
                    </Button>
                  </div>
                </div>
                {(showAll ||
                  permissions.includes("can_view_participants_lists")) && (
                  <Nav tabs className="one_line_parent mb-1">
                    {navItems.map(nav => {
                      return (
                        <NavItem key={nav.id}>
                          <NavLink
                            className={activeTab === nav.id ? "active" : ""}
                            onClick={() => toggleTab(nav.id)}
                          >
                            {t(nav.name)}
                          </NavLink>
                        </NavItem>
                      )
                    })}
                  </Nav>
                )}
                {isShowFilter && (
                  <Filters
                    filters={filters}
                    state={payload.filters}
                    setState={data => {
                      dispatch(
                        setPayload({
                          filters: { ...payload.filters, ...data },
                        })
                      )
                    }}
                    handleSearch={() => setTrigger(!trigger)}
                    handleReset={handleReset}
                  />
                )}
                <Card className="user_personal_info theme_card overflow-x-auto">
                  <CardBody>
                    {loading && practicalParticipants.length === 0 ? (
                      <p>{t("No data found")}</p>
                    ) : (
                      <div className="table-responsive">
                        <Table className="table mb-0">
                          <thead className="table-light full_th">
                            <tr>
                              <th>{t("S. No.")}</th>
                              <th>{t("Participant Name")}</th>
                              <th>{t("National Id")}</th>
                              <th>{t("Mobile Number")}</th>
                              <th>{t("Job")}</th>
                              <th>{t("Is Attended")}</th>
                              <th>{t("Attended By")}</th>
                              <th>{t("Created At")}</th>
                              <th>{t("Action")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {practicalParticipants?.map((item, index) => (
                              <tr key={index}>
                                <th scope="row">{index + 1}</th>
                                <td>{item?.user?.name || "-"}</td>
                                <td>{item?.user?.national_id || "-"}</td>
                                <td>{item?.user?.phone || "-"}</td>
                                <td>{item?.job_details || "-"}</td>
                                <td>
                                  {item?.is_attended
                                    ? t("Yes")
                                    : t("No") || "-"}
                                </td>
                                <td>{item?.attendedBy || "-"}</td>
                                <td>
                                  <p className="ltr fit_content">
                                    {item?.created_at || "-"}
                                  </p>
                                </td>
                                <td>
                                  <div className="action_buttons d-flex align-items-center gap-2">
                                    {[0, 1, 2].includes(activeTab) &&
                                      item?.is_attended &&
                                      item?.status === 0 && (
                                        <Button
                                          color="primary"
                                          onClick={() => {
                                            setSelectedUuid(item.uuid)
                                            setShowAssmentModal(true)
                                          }}
                                          disabled={isLoading}
                                          title={t("Start Assessment")}
                                        >
                                          <ListCheck />
                                        </Button>
                                      )}
                                    {(showAll ||
                                      permissions.includes(
                                        "can_delete_participants"
                                      )) && (
                                      <Button
                                        color="danger"
                                        onClick={() => {
                                          setItemIdToDelete(item.uuid)
                                          setShowDeleteModal(true)
                                        }}
                                        disabled={
                                          isLoading &&
                                          item?.uuid === itemIdToDelete
                                        }
                                        title={t("Delete")}
                                      >
                                        <Trash />
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </CardBody>
                </Card>
                {!loading && practicalParticipants.length > 0 && (
                  <Paginations
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    onPageChange={onPageChange}
                  />
                )}
              </Col>
            </Row>
          </Container>
        )}
      </div>
      <Modal
        isOpen={attendanceModal}
        toggle={() => setAttendanceModal(!attendanceModal)}
        size="lg"
        className=""
        backdrop="static"
      >
        <ModalHeader toggle={() => setAttendanceModal(!attendanceModal)}>
          {t("Mark Attendance")} - {practicalTrainingName}
        </ModalHeader>
        <ModalBody>
          <MarkAttendance
            selectedUuid={uuid}
            selectedItem={{ attendance_count, participants_count }}
            getAllData={getAllData}
            toggle={() => setAttendanceModal(!attendanceModal)}
          />
        </ModalBody>
      </Modal>
      <Modal isOpen={modal} toggle={toggle} size="md" backdrop="static">
        <ModalHeader toggle={toggle}>{t("Add Participant")}</ModalHeader>
        <ModalBody className="pt-0">
          <Formik
            initialValues={{ national_ids: "" }}
            // enableReinitialize={true}
            validationSchema={validationSchema}
            onSubmit={handleAddParticipant}
          >
            {({ errors, touched }) => (
              <Form>
                <Row className="">
                  <Col lg={12}>
                    <div className="form_field">
                      <CustomLabel
                        htmlFor="national_ids"
                        label={t("National Id")}
                        isRequired={true}
                      />
                      <CustomField
                        name="national_ids"
                        type="text"
                        placeholder={t("National Id")}
                        touched={touched}
                        errors={errors}
                      />
                    </div>
                  </Col>
                  <Col lg={12}>
                    <div className="g_end mt-2 gap-3">
                      <button
                        className="main_btn disabled-button fit_content px-4"
                        type="button"
                        onClick={toggle}
                        disabled={isLoading}
                      >
                        {t("Cancel")}
                      </button>
                      <button
                        className="main_btn fit_content px-4"
                        disabled={isLoading}
                      >
                        {t("Add")}
                      </button>
                    </div>
                  </Col>
                </Row>
              </Form>
            )}
          </Formik>
        </ModalBody>
      </Modal>

      {showAssessmentModal && selectedUuid && (
        <MarkAssessmentModal
          isOpen={showAssessmentModal}
          toggle={() => {
            setSelectedUuid(null)
            setShowAssmentModal(!showAssessmentModal)
          }}
          participantID={selectedUuid}
        />
      )}

      <DeleteModal
        show={showDeleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => {
          setItemIdToDelete(null)
          setShowDeleteModal(false)
        }}
        isLoading={isLoading}
        buttonText={t("Delete")}
        bodyTitle={t("Are you sure you want to delete this {{name}}?", {
          name: t("Participant"),
        })}
      />
    </React.Fragment>
  )
}

export default PracticalTrainingParticipants
